import React, { useState, useEffect } from 'react';

const BookingTimer = ({ timerKey, expiryTime }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);

  // Use expiryTime if provided, otherwise use timerKey as expiryTime
  const actualExpiryTime = expiryTime || (timerKey ? parseInt(timerKey, 10) : null);

  useEffect(() => {
    if (!actualExpiryTime) return;

    const updateTimer = () => {
      const now = Date.now();
      const remaining = actualExpiryTime - now;

      if (remaining <= 0) {
        setTimeLeft(0);
        setIsExpired(true);
        return;
      }

      setTimeLeft(remaining);
      setIsExpired(false);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [actualExpiryTime]);

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!timerKey && !expiryTime) {
    return null;
  }

  const getTimerColor = () => {
    if (isExpired) return 'from-red-600 to-red-700';
    if (timeLeft < 300000) return 'from-orange-500 to-red-500'; // Less than 5 minutes
    return 'from-blue-600 to-indigo-600';
  };

  const getTimerIcon = () => {
    if (isExpired) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    );
  };

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-r ${getTimerColor()} text-white shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {getTimerIcon()}
            </div>
            <div className="text-center">
              {isExpired ? (
                <div>
                  <div className="font-bold text-lg">Booking Session Expired</div>
                  <div className="text-sm opacity-90">Please refresh the page to start a new session</div>
                </div>
              ) : (
                <div>
                  <div className="font-bold text-lg">
                    Booking expires in: <span className="font-mono text-xl">{formatTime(timeLeft)}</span>
                  </div>
                  <div className="text-sm opacity-90">Complete your booking before the timer expires</div>
                </div>
              )}
            </div>
            {timeLeft < 300000 && !isExpired && (
              <div className="flex-shrink-0">
                <div className="animate-pulse">
                  <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingTimer;